import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { escapeHtml, openPrintWindow } from '../../utils/printUtils';
import { 
  BookOpen, 
  Calendar,
  Users,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  ChevronRight,
  Clock,
  GraduationCap,
  Baby,
  User,
  Printer,
  Mail,
  CheckCircle2,
  UserCheck,
  RefreshCw,
  Download
} from 'lucide-react';
import DepartmentEmailModule from './DepartmentEmailModule';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

// Age groups for Sunday School
const AGE_GROUPS = [
  { id: 'toddlers', name: 'Toddlers (2-6 years)', icon: Baby, color: 'from-pink-500 to-rose-500', minAge: 2, maxAge: 6 },
  { id: 'juniors', name: 'Juniors (7-10 years)', icon: User, color: 'from-blue-500 to-cyan-500', minAge: 7, maxAge: 10 }
];

const SUNDAY_SCHOOL_TABS = [
  { id: 'syllabus', label: 'Syllabus', icon: BookOpen, color: 'from-green-500 to-emerald-500' },
  { id: 'schedule', label: 'Weekly Schedule', icon: Calendar, color: 'from-blue-500 to-indigo-500' },
  { id: 'attendance', label: 'Attendance', icon: UserCheck, color: 'from-orange-500 to-amber-500' },
  { id: 'teachers', label: 'Teachers', icon: Users, color: 'from-purple-500 to-violet-500' },
  { id: 'email', label: 'Email', icon: Mail, color: 'from-indigo-500 to-purple-500' }
];

// Get upcoming Sundays
const getUpcomingSundays = (count = 12) => {
  const sundays = [];
  const today = new Date();
  let current = new Date(today);
  
  // Find next Sunday
  current.setDate(current.getDate() + ((7 - current.getDay()) % 7 || 7));
  if (current <= today) {
    current.setDate(current.getDate() + 7);
  }
  
  // If today is Sunday, include it
  if (today.getDay() === 0) {
    current = new Date(today);
  }
  
  for (let i = 0; i < count; i++) {
    sundays.push(new Date(current));
    current.setDate(current.getDate() + 7);
  }
  
  return sundays;
};

const SundaySchoolDepartmentSection = ({ adminUser }) => {
  const [activeTab, setActiveTab] = useState('syllabus');
  const [selectedAgeGroup, setSelectedAgeGroup] = useState('toddlers');
  const [syllabus, setSyllabus] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [children, setChildren] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTeacherModal, setShowTeacherModal] = useState(false);
  const [showChildModal, setShowChildModal] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [emailStatus, setEmailStatus] = useState(null);
  const [lessonForm, setLessonForm] = useState({
    date: '',
    age_group: 'toddlers',
    topic: '',
    bible_reading: '',
    memory_verse: '',
    lesson_objectives: '',
    activities: '',
    materials_needed: '',
    teacher_assigned: ''
  });
  const [teacherForm, setTeacherForm] = useState({
    name: '',
    phone: '',
    email: '',
    age_group: 'toddlers'
  });
  const [childForm, setChildForm] = useState({
    name: '',
    age: '',
    parent_name: '',
    parent_phone: '',
    age_group: 'toddlers'
  });

  const upcomingSundays = getUpcomingSundays(12);

  // Load syllabus data
  const loadSyllabus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/sunday-school/syllabus`);
      setSyllabus(response.data || []);
    } catch (error) {
      console.error('Error loading syllabus:', error);
      // Initialize with empty array if endpoint doesn't exist yet
      setSyllabus([]);
    }
    setLoading(false);
  };

  // Load teachers
  const loadTeachers = async () => {
    try {
      const response = await axios.get(`${API}/sunday-school/teachers`);
      setTeachers(response.data || []);
    } catch (error) {
      console.error('Error loading teachers:', error);
      setTeachers([]);
    }
  };

  // Load children
  const loadChildren = async () => {
    try {
      const response = await axios.get(`${API}/sunday-school/children`);
      setChildren(response.data || []);
    } catch (error) {
      console.error('Error loading children:', error);
      setChildren([]);
    }
  };

  // Load attendance for a date
  const loadAttendance = async (date) => {
    try {
      const response = await axios.get(`${API}/sunday-school/attendance`, {
        params: { date, age_group: selectedAgeGroup }
      });
      setAttendance(response.data || []);
    } catch (error) {
      console.error('Error loading attendance:', error);
      setAttendance([]);
    }
  };

  useEffect(() => {
    loadSyllabus();
    loadTeachers();
    loadChildren();
  }, []);

  useEffect(() => {
    if (activeTab === 'attendance') {
      loadAttendance(attendanceDate);
    }
  }, [activeTab, attendanceDate, selectedAgeGroup]);

  // Mark attendance for a child
  const markAttendance = async (childId, present) => {
    try {
      await axios.post(`${API}/sunday-school/attendance`, {
        child_id: childId,
        date: attendanceDate,
        age_group: selectedAgeGroup,
        present
      });
      loadAttendance(attendanceDate);
    } catch (error) {
      console.error('Error marking attendance:', error);
    }
  };

  // Save child
  const handleSaveChild = async () => {
    if (!childForm.name || !childForm.age) {
      alert('Please enter child name and age');
      return;
    }

    try {
      await axios.post(`${API}/sunday-school/children`, {
        ...childForm,
        age_group: selectedAgeGroup
      });
      loadChildren();
      setShowChildModal(false);
      setChildForm({ name: '', age: '', parent_name: '', parent_phone: '', age_group: selectedAgeGroup });
    } catch (error) {
      console.error('Error saving child:', error);
      alert('Failed to add child');
    }
  };

  // Delete child
  const handleDeleteChild = async (childId) => {
    if (!window.confirm('Are you sure you want to remove this child?')) return;
    
    try {
      await axios.delete(`${API}/sunday-school/children/${childId}`);
      loadChildren();
    } catch (error) {
      console.error('Error deleting child:', error);
    }
  };

  // Print syllabus
  const printSyllabus = () => {
    const ageGroup = AGE_GROUPS.find(g => g.id === selectedAgeGroup);
    const filtered = syllabus.filter(l => l.age_group === selectedAgeGroup)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    
    const styles = `
      body { font-family: Arial, sans-serif; padding: 20px; }
      h1 { color: #059669; border-bottom: 2px solid #059669; padding-bottom: 10px; }
      h2 { color: #374151; margin-top: 30px; }
      .lesson { margin-bottom: 30px; padding: 15px; border: 1px solid #e5e7eb; border-radius: 8px; }
      .lesson-date { color: #059669; font-weight: bold; font-size: 14px; }
      .lesson-topic { font-size: 18px; font-weight: bold; margin: 10px 0; }
      .lesson-detail { margin: 8px 0; }
      .label { font-weight: bold; color: #4b5563; }
      .memory-verse { background: #ecfdf5; padding: 10px; border-radius: 5px; font-style: italic; }
      @media print { .no-print { display: none; } }
    `;

    const lessonsHtml = filtered.length === 0
      ? '<p>No lessons scheduled yet.</p>'
      : filtered.map(lesson => `
          <div class="lesson">
            <div class="lesson-date">${escapeHtml(new Date(lesson.date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }))}</div>
            <div class="lesson-topic">${escapeHtml(lesson.topic)}</div>
            ${lesson.bible_reading ? `<div class="lesson-detail"><span class="label">Bible Reading:</span> ${escapeHtml(lesson.bible_reading)}</div>` : ''}
            ${lesson.memory_verse ? `<div class="memory-verse"><span class="label">Memory Verse:</span> ${escapeHtml(lesson.memory_verse)}</div>` : ''}
            ${lesson.lesson_objectives ? `<div class="lesson-detail"><span class="label">Objectives:</span> ${escapeHtml(lesson.lesson_objectives)}</div>` : ''}
            ${lesson.activities ? `<div class="lesson-detail"><span class="label">Activities:</span> ${escapeHtml(lesson.activities)}</div>` : ''}
            ${lesson.materials_needed ? `<div class="lesson-detail"><span class="label">Materials:</span> ${escapeHtml(lesson.materials_needed)}</div>` : ''}
            ${lesson.teacher_assigned ? `<div class="lesson-detail"><span class="label">Teacher:</span> ${escapeHtml(lesson.teacher_assigned)}</div>` : ''}
          </div>
        `).join('');

    const bodyHtml = `
      <h1>Sunday School Syllabus</h1>
      <h2>${escapeHtml(ageGroup?.name)}</h2>
      <p>Generated: ${escapeHtml(new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }))}</p>
      ${lessonsHtml}
    `;

    openPrintWindow(`Sunday School Syllabus - ${ageGroup?.name}`, styles, bodyHtml);
  };

  // Email syllabus to teachers
  const emailSyllabusToTeachers = async () => {
    const ageGroup = AGE_GROUPS.find(g => g.id === selectedAgeGroup);
    const filtered = syllabus.filter(l => l.age_group === selectedAgeGroup)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    const teachersWithEmail = filteredTeachers.filter(t => t.email);
    
    if (teachersWithEmail.length === 0) {
      alert('No teachers with email addresses found for this age group');
      return;
    }

    if (!window.confirm(`Send syllabus to ${teachersWithEmail.length} teacher(s)?`)) return;

    setEmailStatus('sending');
    try {
      await axios.post(`${API}/sunday-school/email-syllabus`, {
        age_group: selectedAgeGroup,
        teacher_emails: teachersWithEmail.map(t => t.email),
        syllabus: filtered
      });
      setEmailStatus('success');
      setTimeout(() => setEmailStatus(null), 3000);
    } catch (error) {
      console.error('Error sending email:', error);
      setEmailStatus('error');
      setTimeout(() => setEmailStatus(null), 3000);
    }
  };

  // Generate yearly syllabus using Bible topics
  const generateYearlySyllabus = async () => {
    const year = new Date().getFullYear();
    if (!window.confirm(`Generate a complete syllabus for ${year} based on Bible topics/themes?\n\nThis will create lessons for all Sundays with age-appropriate Bible content.`)) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/sunday-school/generate-yearly-syllabus`, null, {
        params: { year, age_group: selectedAgeGroup }
      });
      
      if (response.data.success) {
        alert(`Syllabus generated!\nCreated ${response.data.created[selectedAgeGroup] || 0} lessons for ${year}`);
        loadSyllabus();
      }
    } catch (error) {
      console.error('Error generating syllabus:', error);
      alert('Failed to generate syllabus. Please try again.');
    }
    setLoading(false);
  };

  // Download this week's lesson
  const downloadWeeklyLesson = async () => {
    try {
      const response = await axios.get(`${API}/sunday-school/syllabus/download/${selectedAgeGroup}`);
      const lesson = response.data;
      
      const styles = `
        body { font-family: Arial, sans-serif; padding: 30px; max-width: 800px; margin: 0 auto; }
        h1 { color: #059669; border-bottom: 3px solid #059669; padding-bottom: 15px; }
        h2 { color: #1e3a8a; margin-top: 30px; }
        .info-box { background: #f0fdf4; border: 1px solid #bbf7d0; padding: 15px; border-radius: 8px; margin: 20px 0; }
        .memory-verse { background: #fef3c7; border-left: 4px solid #f59e0b; padding: 15px; margin: 20px 0; font-style: italic; }
        .section { margin: 20px 0; }
        .label { font-weight: bold; color: #374151; }
      `;

      const bodyHtml = `
        <h1>Sunday School Lesson</h1>
        <div class="info-box">
          <p><strong>Date:</strong> ${escapeHtml(lesson.title)}</p>
          <p><strong>Age Group:</strong> ${escapeHtml(lesson.age_group)}</p>
          <p><strong>Teacher:</strong> ${escapeHtml(lesson.lesson.teacher || 'Not assigned')}</p>
        </div>
        <h2>${escapeHtml(lesson.lesson.topic)}</h2>
        ${lesson.lesson.bible_reading ? `<div class="section"><span class="label">Bible Reading:</span> ${escapeHtml(lesson.lesson.bible_reading)}</div>` : ''}
        ${lesson.lesson.memory_verse ? `<div class="memory-verse"><span class="label">Memory Verse:</span><br>${escapeHtml(lesson.lesson.memory_verse)}</div>` : ''}
        ${lesson.lesson.objectives ? `<div class="section"><span class="label">Objectives:</span><br>${escapeHtml(lesson.lesson.objectives)}</div>` : ''}
        ${lesson.lesson.activities ? `<div class="section"><span class="label">Activities:</span><br>${escapeHtml(lesson.lesson.activities)}</div>` : ''}
        ${lesson.lesson.materials ? `<div class="section"><span class="label">Materials Needed:</span><br>${escapeHtml(lesson.lesson.materials)}</div>` : ''}
      `;

      openPrintWindow(`Sunday School Lesson - ${lesson.lesson.topic}`, styles, bodyHtml);
    } catch (error) {
      console.error('Error downloading lesson:', error);
      alert('No lesson found for this week. Generate a syllabus first.');
    }
  };

  // Save lesson
  const handleSaveLesson = async () => {
    if (!lessonForm.date || !lessonForm.topic) {
      alert('Please fill in required fields (date and topic)');
      return;
    }

    try {
      if (editingLesson) {
        await axios.put(`${API}/sunday-school/syllabus/${editingLesson.id}`, lessonForm);
      } else {
        await axios.post(`${API}/sunday-school/syllabus`, lessonForm);
      }
      loadSyllabus();
      setShowAddModal(false);
      setEditingLesson(null);
      resetLessonForm();
    } catch (error) {
      console.error('Error saving lesson:', error);
      alert('Failed to save lesson');
    }
  };

  // Delete lesson
  const handleDeleteLesson = async (lessonId) => {
    if (!window.confirm('Are you sure you want to delete this lesson?')) return;
    
    try {
      await axios.delete(`${API}/sunday-school/syllabus/${lessonId}`);
      loadSyllabus();
    } catch (error) {
      console.error('Error deleting lesson:', error);
      alert('Failed to delete lesson');
    }
  };

  // Save teacher
  const handleSaveTeacher = async () => {
    if (!teacherForm.name) {
      alert('Please enter teacher name');
      return;
    }

    try {
      await axios.post(`${API}/sunday-school/teachers`, teacherForm);
      loadTeachers();
      setShowTeacherModal(false);
      setTeacherForm({ name: '', phone: '', email: '', age_group: 'toddlers' });
    } catch (error) {
      console.error('Error saving teacher:', error);
      alert('Failed to save teacher');
    }
  };

  // Delete teacher
  const handleDeleteTeacher = async (teacherId) => {
    if (!window.confirm('Are you sure you want to remove this teacher?')) return;
    
    try {
      await axios.delete(`${API}/sunday-school/teachers/${teacherId}`);
      loadTeachers();
    } catch (error) {
      console.error('Error deleting teacher:', error);
    }
  };

  const resetLessonForm = () => {
    setLessonForm({
      date: '',
      age_group: selectedAgeGroup,
      topic: '',
      bible_reading: '',
      memory_verse: '',
      lesson_objectives: '',
      activities: '',
      materials_needed: '',
      teacher_assigned: ''
    });
  };

  const openEditModal = (lesson) => {
    setEditingLesson(lesson);
    setLessonForm({
      date: lesson.date,
      age_group: lesson.age_group,
      topic: lesson.topic,
      bible_reading: lesson.bible_reading || '',
      memory_verse: lesson.memory_verse || '',
      lesson_objectives: lesson.lesson_objectives || '',
      activities: lesson.activities || '',
      materials_needed: lesson.materials_needed || '',
      teacher_assigned: lesson.teacher_assigned || ''
    });
    setShowAddModal(true);
  };

  // Filter syllabus by age group
  const filteredSyllabus = syllabus.filter(l => l.age_group === selectedAgeGroup);
  const filteredTeachers = teachers.filter(t => t.age_group === selectedAgeGroup);
  const filteredChildren = children.filter(c => c.age_group === selectedAgeGroup);
  const filteredAttendance = attendance.filter(a => a.age_group === selectedAgeGroup);

  // Get attendance status for a child
  const getAttendanceStatus = (childId) => {
    return filteredAttendance.find(a => a.child_id === childId);
  };

  // Get lesson for a specific date
  const getLessonForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return filteredSyllabus.find(l => l.date === dateStr);
  };

  return (
    <div className="space-y-6" data-testid="sunday-school-department-section">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 rounded-2xl sm:rounded-3xl p-4 sm:p-6 text-white shadow-xl"
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className="p-3 rounded-xl bg-white/20 backdrop-blur-md w-fit">
            <GraduationCap className="w-6 h-6 sm:w-8 sm:h-8" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl sm:text-2xl font-bold">Sunday School Department</h2>
            <p className="text-green-100 text-sm sm:text-base">Manage syllabus and schedules for children's classes</p>
          </div>
        </div>
      </motion.div>

      {/* Age Group Selector */}
      <div className="bg-white rounded-2xl p-4 shadow-lg border border-slate-100">
        <label className="block text-sm font-semibold text-slate-700 mb-3">Select Age Group</label>
        <div className="grid grid-cols-2 gap-3">
          {AGE_GROUPS.map(group => {
            const Icon = group.icon;
            return (
              <motion.button
                key={group.id}
                onClick={() => setSelectedAgeGroup(group.id)}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                  selectedAgeGroup === group.id
                    ? `bg-gradient-to-r ${group.color} text-white border-transparent shadow-lg`
                    : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Icon className={`w-6 h-6 ${selectedAgeGroup === group.id ? 'text-white' : 'text-slate-500'}`} />
                <span className={`font-medium ${selectedAgeGroup === group.id ? 'text-white' : 'text-slate-700'}`}>
                  {group.name}
                </span>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="bg-white rounded-2xl p-4 shadow-lg border border-slate-100">
        <div className="flex gap-2 overflow-x-auto">
          {SUNDAY_SCHOOL_TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <motion.button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? `bg-gradient-to-r ${tab.color} text-white shadow-lg`
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {/* Syllabus Tab */}
        {activeTab === 'syllabus' && (
          <motion.div
            key="syllabus"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-4"
          >
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <h3 className="text-lg font-bold text-slate-800">
                Syllabus for {AGE_GROUPS.find(g => g.id === selectedAgeGroup)?.name}
              </h3>
              <div className="flex gap-2 flex-wrap">
                <motion.button
                  onClick={generateYearlySyllabus}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-100 text-amber-700 font-medium hover:bg-amber-200"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid="generate-syllabus-btn"
                >
                  <Calendar className="w-4 h-4" />
                  Generate Year
                </motion.button>
                <motion.button
                  onClick={downloadWeeklyLesson}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl bg-green-100 text-green-700 font-medium hover:bg-green-200"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Download className="w-4 h-4" />
                  This Week
                </motion.button>
                <motion.button
                  onClick={printSyllabus}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-100 text-slate-700 font-medium hover:bg-slate-200"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Printer className="w-4 h-4" />
                  Print
                </motion.button>
                <motion.button
                  onClick={emailSyllabusToTeachers}
                  disabled={emailStatus === 'sending'}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl font-medium ${
                    emailStatus === 'success' ? 'bg-green-100 text-green-700' :
                    emailStatus === 'error' ? 'bg-red-100 text-red-700' :
                    'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  }`}
                  whileHover={{ scale: emailStatus ? 1 : 1.02 }}
                  whileTap={{ scale: emailStatus ? 1 : 0.98 }}
                >
                  {emailStatus === 'sending' ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : emailStatus === 'success' ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : (
                    <Mail className="w-4 h-4" />
                  )}
                  {emailStatus === 'success' ? 'Sent!' : emailStatus === 'error' ? 'Failed' : 'Email Teachers'}
                </motion.button>
                <motion.button
                  onClick={() => {
                    resetLessonForm();
                    setEditingLesson(null);
                    setShowAddModal(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white font-medium shadow-lg"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Plus className="w-4 h-4" />
                  Add Lesson
                </motion.button>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full mx-auto"></div>
                <p className="text-slate-500 mt-2">Loading syllabus...</p>
              </div>
            ) : filteredSyllabus.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-slate-200">
                <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-slate-700 mb-2">No Lessons Added Yet</h3>
                <p className="text-slate-500 mb-4">Create your first lesson for this age group</p>
                <motion.button
                  onClick={() => {
                    resetLessonForm();
                    setShowAddModal(true);
                  }}
                  className="px-6 py-2 rounded-xl bg-green-500 text-white font-medium"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Add First Lesson
                </motion.button>
              </div>
            ) : (
              <div className="grid gap-4">
                {filteredSyllabus
                  .sort((a, b) => new Date(a.date) - new Date(b.date))
                  .map((lesson) => (
                    <motion.div
                      key={lesson.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                              {new Date(lesson.date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}
                            </span>
                            {lesson.teacher_assigned && (
                              <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                                Teacher: {lesson.teacher_assigned}
                              </span>
                            )}
                          </div>
                          <h4 className="text-lg font-bold text-slate-800 mb-2">{lesson.topic}</h4>
                          {lesson.bible_reading && (
                            <p className="text-sm text-slate-600 mb-1">
                              <span className="font-medium">Bible Reading:</span> {lesson.bible_reading}
                            </p>
                          )}
                          {lesson.memory_verse && (
                            <p className="text-sm text-green-700 italic bg-green-50 px-3 py-2 rounded-lg mt-2">
                              <span className="font-medium">Memory Verse:</span> {lesson.memory_verse}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2 ml-4">
                          <motion.button
                            onClick={() => openEditModal(lesson)}
                            className="p-2 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            <Edit className="w-4 h-4" />
                          </motion.button>
                          <motion.button
                            onClick={() => handleDeleteLesson(lesson.id)}
                            className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </motion.button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Weekly Schedule Tab */}
        {activeTab === 'schedule' && (
          <motion.div
            key="schedule"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-4"
          >
            <h3 className="text-lg font-bold text-slate-800">
              Upcoming Sundays - {AGE_GROUPS.find(g => g.id === selectedAgeGroup)?.name}
            </h3>
            
            <div className="grid gap-3">
              {upcomingSundays.map((sunday, index) => {
                const lesson = getLessonForDate(sunday);
                const isPast = sunday < new Date();
                
                return (
                  <motion.div
                    key={sunday.toISOString()}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`bg-white rounded-xl p-4 border-2 ${
                      lesson ? 'border-green-200' : isPast ? 'border-slate-200 opacity-60' : 'border-amber-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-xl ${lesson ? 'bg-green-100' : 'bg-slate-100'}`}>
                          <Calendar className={`w-5 h-5 ${lesson ? 'text-green-600' : 'text-slate-500'}`} />
                        </div>
                        <div>
                          <p className="font-bold text-slate-800">
                            {sunday.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
                          </p>
                          {lesson ? (
                            <p className="text-sm text-green-600 font-medium">{lesson.topic}</p>
                          ) : (
                            <p className="text-sm text-amber-600">No lesson planned</p>
                          )}
                        </div>
                      </div>
                      {!lesson && !isPast && (
                        <motion.button
                          onClick={() => {
                            setLessonForm(prev => ({
                              ...prev,
                              date: sunday.toISOString().split('T')[0],
                              age_group: selectedAgeGroup
                            }));
                            setEditingLesson(null);
                            setShowAddModal(true);
                          }}
                          className="px-4 py-2 rounded-lg bg-amber-100 text-amber-700 text-sm font-medium hover:bg-amber-200"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          + Add Lesson
                        </motion.button>
                      )}
                      {lesson && (
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Teachers Tab */}
        {activeTab === 'teachers' && (
          <motion.div
            key="teachers"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-4"
          >
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-bold text-slate-800">
                Teachers for {AGE_GROUPS.find(g => g.id === selectedAgeGroup)?.name}
              </h3>
              <motion.button
                onClick={() => {
                  setTeacherForm(prev => ({ ...prev, age_group: selectedAgeGroup }));
                  setShowTeacherModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-500 to-violet-500 text-white font-medium shadow-lg"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Plus className="w-4 h-4" />
                Add Teacher
              </motion.button>
            </div>

            {filteredTeachers.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-slate-200">
                <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-slate-700 mb-2">No Teachers Assigned</h3>
                <p className="text-slate-500">Add teachers for this age group</p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {filteredTeachers.map((teacher) => (
                  <motion.div
                    key={teacher.id}
                    className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm"
                    whileHover={{ scale: 1.01 }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                          <span className="text-lg font-bold text-purple-600">
                            {teacher.name?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-800">{teacher.name}</h4>
                          {teacher.phone && <p className="text-sm text-slate-500">{teacher.phone}</p>}
                        </div>
                      </div>
                      <motion.button
                        onClick={() => handleDeleteTeacher(teacher.id)}
                        className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </motion.button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Attendance Tab */}
        {activeTab === 'attendance' && (
          <motion.div
            key="attendance"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-4"
          >
            {/* Date selector and summary */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                  <h3 className="text-lg font-bold text-slate-800">
                    Attendance - {AGE_GROUPS.find(g => g.id === selectedAgeGroup)?.name}
                  </h3>
                  <p className="text-sm text-slate-500">Track class attendance for each Sunday</p>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-slate-400" />
                  <input
                    type="date"
                    className="border-2 border-slate-200 rounded-xl px-4 py-2 focus:border-orange-400 outline-none"
                    value={attendanceDate}
                    onChange={e => setAttendanceDate(e.target.value)}
                  />
                </div>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
                  <p className="text-2xl font-bold text-blue-900">{filteredChildren.length}</p>
                  <p className="text-sm text-blue-700">Registered</p>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
                  <p className="text-2xl font-bold text-green-900">
                    {filteredAttendance.filter(a => a.present).length}
                  </p>
                  <p className="text-sm text-green-700">Present</p>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
                  <p className="text-2xl font-bold text-amber-900">
                    {filteredChildren.length - filteredAttendance.filter(a => a.present).length}
                  </p>
                  <p className="text-sm text-amber-700">Absent</p>
                </div>
              </div>

              {/* Add Child Button */}
              <motion.button
                onClick={() => {
                  setChildForm(prev => ({ ...prev, age_group: selectedAgeGroup }));
                  setShowChildModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-white font-medium"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Plus className="w-4 h-4" />
                Add Child
              </motion.button>
            </div>

            {/* Children List with Attendance */}
            {filteredChildren.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-slate-200">
                <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-slate-700 mb-2">No Children Registered</h3>
                <p className="text-slate-500">Add children to track attendance</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Child</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Age</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Parent</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-slate-600">Attendance</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredChildren.map((child) => {
                      const attendanceRecord = getAttendanceStatus(child.id);
                      const isPresent = attendanceRecord?.present;
                      
                      return (
                        <tr key={child.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                isPresent ? 'bg-green-100' : 'bg-slate-100'
                              }`}>
                                <span className={`text-lg font-bold ${isPresent ? 'text-green-600' : 'text-slate-500'}`}>
                                  {child.name?.charAt(0)?.toUpperCase()}
                                </span>
                              </div>
                              <span className="font-medium text-slate-800">{child.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-600">{child.age} years</td>
                          <td className="px-4 py-3">
                            <p className="text-slate-800">{child.parent_name || '-'}</p>
                            {child.parent_phone && (
                              <p className="text-xs text-slate-500">{child.parent_phone}</p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex justify-center gap-2">
                              <motion.button
                                onClick={() => markAttendance(child.id, true)}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                                  isPresent 
                                    ? 'bg-green-500 text-white' 
                                    : 'bg-slate-100 text-slate-600 hover:bg-green-100 hover:text-green-700'
                                }`}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                              >
                                Present
                              </motion.button>
                              <motion.button
                                onClick={() => markAttendance(child.id, false)}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                                  attendanceRecord && !isPresent 
                                    ? 'bg-red-500 text-white' 
                                    : 'bg-slate-100 text-slate-600 hover:bg-red-100 hover:text-red-700'
                                }`}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                              >
                                Absent
                              </motion.button>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <motion.button
                              onClick={() => handleDeleteChild(child.id)}
                              className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </motion.button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        )}

        {/* Email Tab */}
        {activeTab === 'email' && (
          <motion.div
            key="email"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <DepartmentEmailModule department="sundayschool" adminUser={adminUser} compact={false} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add/Edit Lesson Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-200 sticky top-0 bg-white rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-slate-800">
                    {editingLesson ? 'Edit Lesson' : 'Add New Lesson'}
                  </h3>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="p-2 rounded-lg hover:bg-slate-100"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date *</label>
                  <input
                    type="date"
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-green-400 outline-none"
                    value={lessonForm.date}
                    onChange={e => setLessonForm(prev => ({ ...prev, date: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Topic *</label>
                  <input
                    type="text"
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-green-400 outline-none"
                    placeholder="e.g., The Story of David and Goliath"
                    value={lessonForm.topic}
                    onChange={e => setLessonForm(prev => ({ ...prev, topic: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Bible Reading</label>
                  <input
                    type="text"
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-green-400 outline-none"
                    placeholder="e.g., 1 Samuel 17:1-50"
                    value={lessonForm.bible_reading}
                    onChange={e => setLessonForm(prev => ({ ...prev, bible_reading: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Memory Verse</label>
                  <textarea
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-green-400 outline-none resize-none"
                    rows={2}
                    placeholder="e.g., The Lord is my shepherd... - Psalm 23:1"
                    value={lessonForm.memory_verse}
                    onChange={e => setLessonForm(prev => ({ ...prev, memory_verse: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Lesson Objectives</label>
                  <textarea
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-green-400 outline-none resize-none"
                    rows={2}
                    placeholder="What children should learn from this lesson"
                    value={lessonForm.lesson_objectives}
                    onChange={e => setLessonForm(prev => ({ ...prev, lesson_objectives: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Activities</label>
                  <textarea
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-green-400 outline-none resize-none"
                    rows={2}
                    placeholder="Songs, crafts, games, etc."
                    value={lessonForm.activities}
                    onChange={e => setLessonForm(prev => ({ ...prev, activities: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Materials Needed</label>
                  <input
                    type="text"
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-green-400 outline-none"
                    placeholder="Crayons, paper, stickers, etc."
                    value={lessonForm.materials_needed}
                    onChange={e => setLessonForm(prev => ({ ...prev, materials_needed: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Assign Teacher</label>
                  <select
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-green-400 outline-none"
                    value={lessonForm.teacher_assigned}
                    onChange={e => setLessonForm(prev => ({ ...prev, teacher_assigned: e.target.value }))}
                  >
                    <option value="">Select Teacher</option>
                    {filteredTeachers.map(t => (
                      <option key={t.id} value={t.name}>{t.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="p-6 border-t border-slate-200 flex gap-3">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 py-2.5 rounded-xl border-2 border-slate-300 text-slate-600 font-medium hover:bg-slate-100"
                >
                  Cancel
                </button>
                <motion.button
                  onClick={handleSaveLesson}
                  className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Save className="w-4 h-4" />
                  {editingLesson ? 'Update' : 'Save'} Lesson
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Teacher Modal */}
      <AnimatePresence>
        {showTeacherModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowTeacherModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-md"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-slate-800">Add Teacher</h3>
                  <button
                    onClick={() => setShowTeacherModal(false)}
                    className="p-2 rounded-lg hover:bg-slate-100"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                  <input
                    type="text"
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-purple-400 outline-none"
                    placeholder="Teacher's full name"
                    value={teacherForm.name}
                    onChange={e => setTeacherForm(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-purple-400 outline-none"
                    placeholder="Phone number"
                    value={teacherForm.phone}
                    onChange={e => setTeacherForm(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input
                    type="email"
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-purple-400 outline-none"
                    placeholder="Email address"
                    value={teacherForm.email}
                    onChange={e => setTeacherForm(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
              </div>

              <div className="p-6 border-t border-slate-200 flex gap-3">
                <button
                  onClick={() => setShowTeacherModal(false)}
                  className="flex-1 py-2.5 rounded-xl border-2 border-slate-300 text-slate-600 font-medium hover:bg-slate-100"
                >
                  Cancel
                </button>
                <motion.button
                  onClick={handleSaveTeacher}
                  className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-violet-500 text-white font-bold flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Save className="w-4 h-4" />
                  Add Teacher
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Add Child Modal */}
        {showChildModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowChildModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-md"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-slate-800">Add Child</h3>
                  <button
                    onClick={() => setShowChildModal(false)}
                    className="p-2 rounded-lg hover:bg-slate-100"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Child's Name *</label>
                  <input
                    type="text"
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-400 outline-none"
                    placeholder="Full name"
                    value={childForm.name}
                    onChange={e => setChildForm(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Age *</label>
                  <input
                    type="number"
                    min="2"
                    max="10"
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-400 outline-none"
                    placeholder="Age in years"
                    value={childForm.age}
                    onChange={e => setChildForm(prev => ({ ...prev, age: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Parent/Guardian Name</label>
                  <input
                    type="text"
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-400 outline-none"
                    placeholder="Parent's name"
                    value={childForm.parent_name}
                    onChange={e => setChildForm(prev => ({ ...prev, parent_name: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Parent's Phone</label>
                  <input
                    type="tel"
                    className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-400 outline-none"
                    placeholder="Phone number"
                    value={childForm.parent_phone}
                    onChange={e => setChildForm(prev => ({ ...prev, parent_phone: e.target.value }))}
                  />
                </div>
              </div>

              <div className="p-6 border-t border-slate-200 flex gap-3">
                <button
                  onClick={() => setShowChildModal(false)}
                  className="flex-1 py-2.5 rounded-xl border-2 border-slate-300 text-slate-600 font-medium hover:bg-slate-100"
                >
                  Cancel
                </button>
                <motion.button
                  onClick={handleSaveChild}
                  className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Save className="w-4 h-4" />
                  Add Child
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SundaySchoolDepartmentSection;
